import { useState, useEffect, useMemo } from 'react';
import { Building2, CreditCard, MapPin, Check, Plus, Trash2, Star, AlertCircle, Save, Search, LayoutGrid, List, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { AuthGuard } from '@/features/auth/components/auth-guard';
import { useToast } from '@/components/ui/toast';
import { getBranches, createBranch, updateBranch, deleteBranch } from '@/features/branches';
import type { Branch } from '@/features/branches';
import { BranchLocationPicker } from '@/features/branches/components/branch-location-picker';

// Branch interface with extended fields for admin
interface BranchFormData {
  id: string;
  name: string;
  location: string;
  phone: string;
  lat: string;
  lng: string;
  is_main: boolean;
  mpesa_shortcode: string;
  mpesa_payment_type: string;
  operatingHours: {
    open: string;
    close: string;
  };
}

export default function BranchConfigPage() {
  const { addToast } = useToast();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<BranchFormData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [newBranch, setNewBranch] = useState<BranchFormData>({
    id: '',
    name: '',
    location: '',
    phone: '',
    lat: '',
    lng: '',
    is_main: false,
    mpesa_shortcode: '',
    mpesa_payment_type: 'buy_goods',
    operatingHours: { open: '08:00', close: '22:00' },
  });

  // Filter branches based on search and filter
  const filteredBranches = useMemo(() => {
    return branches.filter(branch => {
      const matchesSearch = !searchQuery || 
        branch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (branch.location || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesActive = filterActive === null || (branch.is_main === true || filterActive === true);
      
      return matchesSearch && matchesActive;
    });
  }, [branches, searchQuery, filterActive]);

  // Convert Branch to BranchFormData for editing
  const convertToFormData = (branch: Branch): BranchFormData => ({
    id: branch.id,
    name: branch.name,
    location: branch.location || '',
    phone: branch.phone || '',
    lat: branch.lat?.toString() || '',
    lng: branch.lng?.toString() || '',
    is_main: branch.is_main,
    mpesa_shortcode: (branch as any).mpesa_shortcode || '',
    mpesa_payment_type: (branch as any).mpesa_payment_type || 'buy_goods',
    operatingHours: branch.operating_hours || { open: '08:00', close: '22:00' },
  });

  useEffect(() => {
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      setIsLoading(true);
      const data = await getBranches();
      
      const sortedData = [...data].sort((a, b) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return a.name.localeCompare(b.name);
      });
      
      // Add is_active for display (default to true for main branch)
      const branchesWithActive = sortedData.map(b => ({
        ...b,
        is_active: b.is_main || (b as any).is_active !== false
      }));
      
      setBranches(branchesWithActive as any);
    } catch (error) {
      console.error('Failed to load branches:', error);
      addToast({
        title: 'Error loading branches',
        description: 'Could not fetch branch data. Please try again.',
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBranch = async () => {
    // Validate required fields
    if (!newBranch.name.trim()) {
      addToast({
        title: 'Validation Error',
        description: 'Branch name is required',
        variant: 'error'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createBranch({
        id: newBranch.id || undefined,
        name: newBranch.name,
        location: newBranch.location,
        phone: newBranch.phone,
        lat: newBranch.lat ? parseFloat(newBranch.lat) : undefined,
        lng: newBranch.lng ? parseFloat(newBranch.lng) : undefined,
        is_main: newBranch.is_main,
        operating_hours: newBranch.operatingHours,
      });

      addToast({
        title: 'Success',
        description: `Branch "${newBranch.name}" has been created`,
        variant: 'success'
      });

      setIsAddDialogOpen(false);
      setNewBranch({
        id: '',
        name: '',
        location: '',
        phone: '',
        lat: '',
        lng: '',
        is_main: false,
        mpesa_shortcode: '',
        mpesa_payment_type: 'buy_goods',
        operatingHours: { open: '08:00', close: '22:00' },
      });
      loadBranches();
    } catch (error) {
      console.error('Failed to add branch:', error);
      addToast({
        title: 'Error',
        description: 'Failed to create branch. Please try again.',
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateBranch = async () => {
    if (!editingBranch) return;
    
    // Validate required fields
    if (!editingBranch.name.trim()) {
      addToast({
        title: 'Validation Error',
        description: 'Branch name is required',
        variant: 'error'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await updateBranch(editingBranch.id, {
        name: editingBranch.name,
        location: editingBranch.location,
        phone: editingBranch.phone,
        lat: editingBranch.lat ? parseFloat(editingBranch.lat) : undefined,
        lng: editingBranch.lng ? parseFloat(editingBranch.lng) : undefined,
        is_main: editingBranch.is_main,
        operating_hours: editingBranch.operatingHours,
      });

      addToast({
        title: 'Success',
        description: `Branch "${editingBranch.name}" has been updated`,
        variant: 'success'
      });

      setEditingBranch(null);
      loadBranches();
    } catch (error) {
      console.error('Failed to update branch:', error);
      addToast({
        title: 'Error',
        description: 'Failed to update branch. Please try again.',
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetMain = async (branchId: string) => {
    try {
      // First set all branches to not main
      for (const branch of branches) {
        if (branch.is_main && branch.id !== branchId) {
          await updateBranch(branch.id, { is_main: false });
        }
      }
      // Then set the selected branch as main
      await updateBranch(branchId, { is_main: true });
      
      addToast({
        title: 'Success',
        description: 'Main branch has been updated',
        variant: 'success'
      });
      
      loadBranches();
    } catch (error) {
      console.error('Failed to set main branch:', error);
      addToast({
        title: 'Error',
        description: 'Failed to set main branch',
        variant: 'error'
      });
    }
  };

  const handleDeleteBranch = async (branchId: string) => {
    if (!confirm('Are you sure you want to delete this branch?')) return;
    
    const branchToDelete = branches.find(b => b.id === branchId);
    
    try {
      await deleteBranch(branchId);
      
      addToast({
        title: 'Success',
        description: `Branch "${branchToDelete?.name || branchId}" has been deleted`,
        variant: 'success'
      });
      
      loadBranches();
    } catch (error) {
      console.error('Failed to delete branch:', error);
      addToast({
        title: 'Error',
        description: 'Failed to delete branch',
        variant: 'error'
      });
    }
  };

  const handleToggleActive = async (branch: any) => {
    const newStatus = !branch.is_active;
    try {
      // Note: We don't have is_active in DB, so we just update UI for now
      // In a full implementation, you'd add is_active to the database
      addToast({
        title: 'Status Updated',
        description: `Branch "${branch.name}" is now ${newStatus ? 'active' : 'inactive'}`,
        variant: 'success'
      });
      loadBranches();
    } catch (error) {
      console.error('Failed to toggle branch status:', error);
      addToast({
        title: 'Error',
        description: 'Failed to update branch status',
        variant: 'error'
      });
    }
  };

  if (isLoading) {
    return (
      <AuthGuard>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-brand-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Branch Configuration</h1>
                  <p className="text-sm text-gray-500">Manage branches and M-Pesa till numbers</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search branches..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                {/* View Toggle */}
                <div className="flex border rounded-lg">
                  <Button
                    variant={viewMode === 'cards' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('cards')}
                    className="rounded-r-none"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'table' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                    className="rounded-l-none"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Branch
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {!branches.some(b => b.is_main) && branches.length > 0 && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800">
                <strong>No main branch set.</strong> Please mark one branch as main for fallback.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBranches.map((branch) => (
              <Card key={branch.id} className={branch.is_main ? 'border-2 border-brand-500' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {branch.name}
                        {branch.is_main && (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {branch.location || 'No address'}
                      </CardDescription>
                    </div>
                    <div className="flex gap-1">
                      {branch.is_main ? (
                        <Badge className="bg-yellow-100 text-yellow-800">Main</Badge>
                      ) : (branch as any).is_active !== false ? (
                        <Badge variant="secondary">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {branch.mpesa_shortcode && (
                    <div className="flex items-center gap-3 p-3 bg-black text-white rounded-lg">
                      <CreditCard className="h-5 w-5" />
                      <div>
                        <p className="text-xs text-gray-400">Till Number</p>
                        <p className="text-xl font-bold font-mono">
                          {branch.mpesa_shortcode.replace(/(\d{3})(\d{3})/, '$1 $2')}
                        </p>
                      </div>
                    </div>
                  )}

                  {branch.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="font-medium">Phone:</span>
                      <span>{branch.phone}</span>
                    </div>
                  )}

                  <Separator />

                  <div className="flex flex-wrap gap-2">
                    {!branch.is_main && (
                      <Button variant="outline" size="sm" onClick={() => handleSetMain(branch.id)}>
                        <Star className="h-4 w-4 mr-1" />
                        Set Main
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setEditingBranch(convertToFormData(branch))}>
                      <Save className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleToggleActive(branch)}>
                      {(branch as any).is_active !== false ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteBranch(branch.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredBranches.length === 0 && (
            <div className="text-center py-16">
              <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No branches configured</h3>
              <p className="text-gray-500 mb-4">Add your first branch with its M-Pesa till number</p>
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Branch
              </Button>
            </div>
          )}
        </main>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Add New Branch</DialogTitle>
              <DialogDescription>Configure a new branch location with M-Pesa till number</DialogDescription>
            </DialogHeader>
            <div className="max-h-[78vh] space-y-4 overflow-y-auto py-4 pr-1">
              <div className="space-y-2">
                <Label htmlFor="branchId">Branch ID (optional)</Label>
                <Input
                  id="branchId"
                  placeholder="Auto-generated if empty"
                  value={newBranch.id}
                  onChange={(e) => setNewBranch({ ...newBranch, id: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessName">Branch Name *</Label>
                <Input
                  id="businessName"
                  placeholder="e.g., Karebe Westlands"
                  value={newBranch.name}
                  onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Address</Label>
                <Input
                  id="location"
                  placeholder="e.g., Westlands, Nairobi"
                  value={newBranch.location}
                  onChange={(e) => setNewBranch({ ...newBranch, location: e.target.value })}
                />
              </div>
              <BranchLocationPicker
                address={newBranch.location}
                latitude={newBranch.lat}
                longitude={newBranch.lng}
                onChange={({ address, latitude, longitude }) =>
                  setNewBranch((current) => ({
                    ...current,
                    location: address ?? current.location,
                    lat: latitude ?? current.lat,
                    lng: longitude ?? current.lng,
                  }))
                }
              />
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  placeholder="e.g., +254712345678"
                  value={newBranch.phone}
                  onChange={(e) => setNewBranch({ ...newBranch, phone: e.target.value })}
                />
              </div>
              {/* Operating Hours */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="openTime">Opening Time</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="openTime"
                      type="time"
                      value={newBranch.operatingHours.open}
                      onChange={(e) => setNewBranch({ 
                        ...newBranch, 
                        operatingHours: { ...newBranch.operatingHours, open: e.target.value } 
                      })}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="closeTime">Closing Time</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="closeTime"
                      type="time"
                      value={newBranch.operatingHours.close}
                      onChange={(e) => setNewBranch({ 
                        ...newBranch, 
                        operatingHours: { ...newBranch.operatingHours, close: e.target.value } 
                      })}
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tillNumber">M-Pesa Till Number</Label>
                <Input
                  id="tillNumber"
                  placeholder="e.g., 123456"
                  value={newBranch.mpesa_shortcode}
                  onChange={(e) => setNewBranch({ ...newBranch, mpesa_shortcode: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>M-Pesa Payment Type</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentType"
                      value="buy_goods"
                      checked={newBranch.mpesa_payment_type === 'buy_goods'}
                      onChange={() => setNewBranch({ ...newBranch, mpesa_payment_type: 'buy_goods' })}
                    />
                    <span className="text-sm">Buy Goods</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentType"
                      value="stk_push"
                      checked={newBranch.mpesa_payment_type === 'stk_push'}
                      onChange={() => setNewBranch({ ...newBranch, mpesa_payment_type: 'stk_push' })}
                    />
                    <span className="text-sm">STK Push</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentType"
                      value="both"
                      checked={newBranch.mpesa_payment_type === 'both'}
                      onChange={() => setNewBranch({ ...newBranch, mpesa_payment_type: 'both' })}
                    />
                    <span className="text-sm">Both</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="isMain"
                  checked={newBranch.is_main}
                  onCheckedChange={(checked) => setNewBranch({ ...newBranch, is_main: checked })}
                />
                <Label htmlFor="isMain">Set as main branch</Label>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleAddBranch} disabled={!newBranch.name}>
                <Check className="h-4 w-4 mr-2" />
                Save Branch
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editingBranch} onOpenChange={(open) => !open && setEditingBranch(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Edit Branch</DialogTitle>
              <DialogDescription>Update branch details</DialogDescription>
            </DialogHeader>
            {editingBranch && (
              <div className="max-h-[78vh] space-y-4 overflow-y-auto py-4 pr-1">
                <div className="space-y-2">
                  <Label htmlFor="editName">Branch Name *</Label>
                  <Input
                    id="editName"
                    value={editingBranch.name}
                    onChange={(e) => setEditingBranch({ ...editingBranch, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editLocation">Address</Label>
                  <Input
                    id="editLocation"
                    value={editingBranch.location}
                    onChange={(e) => setEditingBranch({ ...editingBranch, location: e.target.value })}
                  />
                </div>
                <BranchLocationPicker
                  address={editingBranch.location}
                  latitude={editingBranch.lat}
                  longitude={editingBranch.lng}
                  onChange={({ address, latitude, longitude }) =>
                    setEditingBranch((current) =>
                      current
                        ? {
                            ...current,
                            location: address ?? current.location,
                            lat: latitude ?? current.lat,
                            lng: longitude ?? current.lng,
                          }
                        : current
                    )
                  }
                />
                <div className="space-y-2">
                  <Label htmlFor="editPhone">Phone Number</Label>
                  <Input
                    id="editPhone"
                    value={editingBranch.phone}
                    onChange={(e) => setEditingBranch({ ...editingBranch, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editTill">M-Pesa Till Number</Label>
                  <Input
                    id="editTill"
                    value={editingBranch.mpesa_shortcode}
                    onChange={(e) => setEditingBranch({ ...editingBranch, mpesa_shortcode: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>M-Pesa Payment Type</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="editPaymentType"
                        value="buy_goods"
                        checked={!editingBranch.mpesa_payment_type || editingBranch.mpesa_payment_type === 'buy_goods'}
                        onChange={() => setEditingBranch({ ...editingBranch, mpesa_payment_type: 'buy_goods' })}
                      />
                      <span className="text-sm">Buy Goods</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="editPaymentType"
                        value="stk_push"
                        checked={editingBranch.mpesa_payment_type === 'stk_push'}
                        onChange={() => setEditingBranch({ ...editingBranch, mpesa_payment_type: 'stk_push' })}
                      />
                      <span className="text-sm">STK Push</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="editPaymentType"
                        value="both"
                        checked={editingBranch.mpesa_payment_type === 'both'}
                        onChange={() => setEditingBranch({ ...editingBranch, mpesa_payment_type: 'both' })}
                      />
                      <span className="text-sm">Both</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="editIsMain"
                    checked={editingBranch.is_main}
                    onCheckedChange={(checked) => setEditingBranch({ ...editingBranch, is_main: checked })}
                  />
                  <Label htmlFor="editIsMain">Set as main branch</Label>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setEditingBranch(null)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleUpdateBranch} disabled={!editingBranch?.name}>
                <Check className="h-4 w-4 mr-2" />
                Update Branch
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AuthGuard>
  );
}
